import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sparkles, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface AestheticProtocolCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function AestheticProtocolCard({ records, onRecordsChange }: AestheticProtocolCardProps) {
  const aestheticRecords = records.filter(r => r.type === 'aesthetic_procedure');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (aestheticRecords.length === 0) return null;

  const handleEdit = (idx: number) => {
    const originalIndex = records.indexOf(aestheticRecords[idx]);
    const record = records[originalIndex];
    setEditForm({
      ...record,
      products: Array.isArray(record.products) ? record.products.join(', ') : record.products || ''
    });
    setEditingIndex(originalIndex);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...records];
    const formToSave = {
      ...editForm,
      products: editForm.products
        ? editForm.products.split(',').map((p: string) => p.trim()).filter(Boolean)
        : []
    };
    updated[editingIndex] = formToSave;
    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Registro atualizado!");
  };

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(aestheticRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Registro excluído!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-rose-600" />
        <h3 className="text-lg font-semibold text-rose-600">Protocolo Estético</h3>
        <Badge variant="secondary">{aestheticRecords.length} procedimentos</Badge>
      </div>

      <div className="space-y-3">
        {aestheticRecords.map((record, idx) => (
          <Card key={idx} className="border-l-4 border-l-rose-600">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-foreground">{record.procedure}</span>
                  <div className="flex items-center gap-2">
                    {record.date && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(record.date).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(idx)}
                      className="text-primary hover:bg-primary/10"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(idx)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {record.area && (
                  <div>
                    <span className="text-sm text-muted-foreground">Área: </span>
                    <span className="text-sm font-medium">{record.area}</span>
                  </div>
                )}

                {record.products && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Produtos Utilizados</p>
                    <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                      {(Array.isArray(record.products) ? record.products : [record.products]).map((product: string, i: number) => (
                        <li key={i}>{product}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {record.parameters && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Parâmetros:</span>
                    <span className="text-foreground">{record.parameters}</span>
                  </div>
                )}

                {record.skin_reaction && (
                  <div className="bg-rose-50 border border-rose-200 rounded p-2">
                    <p className="text-xs text-rose-700">
                      🔍 <span className="font-medium">Reação:</span> {record.skin_reaction}
                    </p>
                  </div>
                )}

                {record.post_care && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <p className="text-xs text-amber-700">
                      ⚠️ <span className="font-medium">Cuidados:</span> {record.post_care}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Editar Protocolo Estético</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid gap-2">
              <Label>Procedimento</Label>
              <Input
                value={editForm.procedure || ""}
                onChange={(e) => setEditForm({ ...editForm, procedure: e.target.value })}
                placeholder="Ex: Limpeza de Pele, Peeling"
              />
            </div>

            <div className="grid gap-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={editForm.date || ""}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Área Tratada</Label>
              <Input
                value={editForm.area || ""}
                onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
                placeholder="Ex: Rosto, Corpo"
              />
            </div>

            <div className="grid gap-2">
              <Label>Produtos Utilizados (separados por vírgula)</Label>
              <Textarea
                value={editForm.products || ""}
                onChange={(e) => setEditForm({ ...editForm, products: e.target.value })}
                placeholder="Ex: Ácido Glicólico 20%, Vitamina C"
              />
            </div>

            <div className="grid gap-2">
              <Label>Parâmetros</Label>
              <Input
                value={editForm.parameters || ""}
                onChange={(e) => setEditForm({ ...editForm, parameters: e.target.value })}
                placeholder="Ex: 15 minutos, temperatura ambiente"
              />
            </div>

            <div className="grid gap-2">
              <Label>Reação da Pele</Label>
              <Textarea
                value={editForm.skin_reaction || ""}
                onChange={(e) => setEditForm({ ...editForm, skin_reaction: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Cuidados Pós-Procedimento</Label>
              <Textarea
                value={editForm.post_care || ""}
                onChange={(e) => setEditForm({ ...editForm, post_care: e.target.value })}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}